import { useEffect, useState } from 'react';
import type { Person, PersonInput } from '@gm-binder/shared';
import { api } from '../api/client';
import { AppShell } from '../components/layout/AppShell';
import {
  PageHeader,
  Card,
  Button,
  IconButton,
  Modal,
  Field,
  Input,
  Textarea,
  LoadingState,
  EmptyState,
} from '../components/ui';
import { useUiStore } from '../store/uiStore';
import { hasPhone, smsLink, whatsappLink } from '../lib/contact';
import styles from './PeoplePage.module.css';

const EMPTY: PersonInput = { name: '', phone: '', email: '', notes: '' };

export function PeoplePage() {
  const [people, setPeople] = useState<Person[] | null>(null);
  const [editing, setEditing] = useState<Person | 'new' | null>(null);
  const toast = useUiStore((s) => s.toast);

  const reload = async () => {
    try {
      setPeople(await api.listPeople());
    } catch {
      setPeople([]);
    }
  };

  useEffect(() => {
    void reload();
  }, []);

  const remove = async (p: Person) => {
    if (!confirm(`Eliminare ${p.name} dalla rubrica?`)) return;
    await api.deletePerson(p.id);
    setPeople((ps) => ps?.filter((x) => x.id !== p.id) ?? null);
  };

  const save = async (input: PersonInput) => {
    try {
      if (editing && editing !== 'new') {
        await api.updatePerson(editing.id, input);
        toast('Persona aggiornata', 'success');
      } else {
        await api.createPerson(input);
        toast('Persona aggiunta', 'success');
      }
      setEditing(null);
      await reload();
    } catch {
      toast('Salvataggio fallito', 'danger');
    }
  };

  return (
    <AppShell>
      <PageHeader
        eyebrow="Giocatori"
        title="Persone"
        subtitle="La tua rubrica di giocatori: contatti e collegamenti ai PG."
        actions={
          <Button variant="primary" icon="✦" onClick={() => setEditing('new')}>
            Nuova persona
          </Button>
        }
      />

      {people == null ? (
        <LoadingState />
      ) : people.length === 0 ? (
        <EmptyState
          icon="🧑‍🤝‍🧑"
          title="Nessuna persona"
          description="Aggiungi i tuoi giocatori per pianificare le sessioni e collegarli ai personaggi."
          actions={
            <Button variant="primary" onClick={() => setEditing('new')}>
              Aggiungi persona
            </Button>
          }
        />
      ) : (
        <div className={styles.grid}>
          {people.map((p) => (
            <Card key={p.id} padded={false}>
              <div className={styles.row}>
                <span className={styles.avatar} aria-hidden>
                  {p.name.slice(0, 1).toUpperCase()}
                </span>
                <div style={{ minWidth: 0 }}>
                  <div className={styles.name}>{p.name}</div>
                  <div className={styles.sub}>
                    {hasPhone(p.phone) && (
                      <span className={styles.contacts}>
                        <span>📱 {p.phone}</span>
                        <a
                          className={styles.chip}
                          href={whatsappLink(p.phone, `Ciao ${p.name}!`)}
                          target="_blank"
                          rel="noreferrer"
                        >
                          WhatsApp
                        </a>
                        <a className={styles.chip} href={smsLink(p.phone, `Ciao ${p.name}!`)}>
                          SMS
                        </a>
                      </span>
                    )}
                    {p.email && <span>✉️ {p.email}</span>}
                    {p.notes && <span>📝 {p.notes}</span>}
                  </div>
                </div>
                <div className={styles.actions}>
                  <Button size="sm" variant="ghost" onClick={() => setEditing(p)}>
                    Modifica
                  </Button>
                  <IconButton label="Elimina" size="sm" onClick={() => remove(p)}>
                    🗑️
                  </IconButton>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {editing != null && (
        <PersonModal
          person={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSave={save}
        />
      )}
    </AppShell>
  );
}

function PersonModal({
  person,
  onClose,
  onSave,
}: {
  person: Person | null;
  onClose: () => void;
  onSave: (input: PersonInput) => void | Promise<void>;
}) {
  const [form, setForm] = useState<PersonInput>(
    person
      ? { name: person.name, phone: person.phone, email: person.email, notes: person.notes }
      : EMPTY,
  );
  const set = (k: keyof PersonInput, v: string) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <Modal open onClose={onClose} title={person ? 'Modifica persona' : 'Nuova persona'} width={460}>
      <form
        className={styles.form}
        onSubmit={(e) => {
          e.preventDefault();
          if (!form.name?.trim()) return;
          void onSave(form);
        }}
      >
        <Field label="Nome">
          <Input
            value={form.name}
            autoFocus
            onChange={(e) => set('name', e.target.value)}
            placeholder="Es. Marco Rossi"
          />
        </Field>
        <Field label="Cellulare" hint="Usato per i link WhatsApp/SMS (includi il prefisso, es. +39).">
          <Input
            value={form.phone}
            onChange={(e) => set('phone', e.target.value)}
            placeholder="+39 333 1234567"
            inputMode="tel"
          />
        </Field>
        <Field label="Email">
          <Input
            value={form.email}
            onChange={(e) => set('email', e.target.value)}
            placeholder="marco@example.com"
            type="email"
          />
        </Field>
        <Field label="Note">
          <Textarea
            value={form.notes}
            onChange={(e) => set('notes', e.target.value)}
            placeholder="Preferenze, allergie, disponibilità…"
            rows={3}
          />
        </Field>
        <div className={styles.formActions}>
          <Button type="button" variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button type="submit" variant="primary" disabled={!form.name?.trim()}>
            Salva
          </Button>
        </div>
      </form>
    </Modal>
  );
}
